import { Button, Checkbox, Form, Select, Space } from 'antd';
import { FC, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useHistory } from 'react-router-dom';
import { CreateButton } from 'components/AdsAutomation/AdsList.styled';
import { useAdsPageHeader } from 'components/AdsAutomation/useAdsPageHeader';
import { ROUTES } from 'shared/constants/ROUTES';
import { IPagination } from 'shared/hooks/usePagination';
import { IProgramSetup, IProgramSetupResponsePayload } from 'shared/interfaces/IProgramSetup';
import { IProgram } from 'shared/interfaces/IProgram';
import { ITableData } from 'shared/interfaces/utils/ITable';
import Table from 'shared/theme/elements/Table/Table';
import CustomSelect from 'shared/theme/elements/Select/Select';
// import { IKeyValue } from 'shared/interfaces/utils/IKeyValue';

import { LIST_COLUMN } from './ListColumn';
import { FilterFld, FilterLabel, ListBar, ListPage, TableCard } from '../ProgramSetup/ProgramSetup.styled';
// new add
import {
  ProgramSetupFilter,
  ProgramSetupLoading,
  ProgramSetupSearch,
  ProgramSetupSelectOptions,
} from './ListContainer';
import { toTitleCase } from 'shared/utils/toTitleCase';
import { ProgramSetupChannelSettings } from 'components/ProgramSetupChannelSettings';

interface Props {
  data?: IProgramSetupResponsePayload;
  pagination: IPagination;
  onDelete: (
    id: string,
    channel: Pick<IProgramSetup, 'channel_details'>['channel_details'],
    program: Pick<IProgramSetup, 'program_details'>['program_details'],
  ) => void;
  onEdit: (id: string) => void;
  onFilter: (key: keyof ProgramSetupFilter, value?: any) => void;
  filters: ProgramSetupFilter;
  options: ProgramSetupSelectOptions;
  loading: ProgramSetupLoading;
  onSearch: (key: keyof ProgramSetupSearch, value: any) => void;
  search: ProgramSetupSearch;
  handleSelect: (record: any, selected: string[]) => void;
  toggleSelectAll: (rowsChecked: any) => void;
  selectedRowKeys: string[];
  newSelectedRows: string[];
  locationState: { machine_ip: string; machine_type: string };
  IPOption?: { value: string; label: string }[];
  selectedIP: string;
  handleChangeIP: (value: string) => void;
  transferMachineIP: () => void;
  channelRepresentative?: IProgramSetup;
  channelSettingsLoading?: boolean;
  programById: Record<string, IProgram>;
}

const ListView: FC<Props> = ({
  data,
  onDelete,
  onEdit,
  onFilter,
  onSearch,
  search,
  filters,
  options,
  loading,
  pagination,
  handleSelect,
  toggleSelectAll,
  selectedRowKeys,
  newSelectedRows,
  locationState,
  IPOption,
  selectedIP,
  handleChangeIP,
  transferMachineIP,
  channelRepresentative,
  channelSettingsLoading,
  programById,
}) => {
  const tableData = useMemo(() => {
    const results = data?.data ? data.data : [];

    return results.map((result: IProgramSetup) => {
      return {
        ...result,
        key: result._id,
      };
    });
  }, [data]) as ITableData[];

  const headerCheckbox = (
    <Checkbox
      checked={newSelectedRows?.every((v) => selectedRowKeys.includes(v)) && selectedRowKeys.length !== 0}
      onChange={(e) => toggleSelectAll(e)}
      indeterminate={!newSelectedRows?.every((v) => selectedRowKeys.includes(v)) && selectedRowKeys.length !== 0}
    />
  );

  const rowSelection = {
    selectedRowKeys,
    type: 'checkbox',
    onSelect: handleSelect,
    columnTitle: headerCheckbox,
    getCheckboxProps: (record) => ({
      disabled: record.mmi_service_tag,
    }),
  };

  // Convert channel options to IKeyValue format for custom Select component
  const channelItems = useMemo(() => {
    return (
      options.channel?.map((option) => ({
        id: option.value as string,
        value: option.label as string,
        disabled: option.disabled,
      })) || []
    );
  }, [options.channel]);

  // Ensure the selected value always has a label in the items
  const selectedChannel = channelItems.find((opt) => opt.id === filters.channel);
  // Find the channel_name from the data if not in options
  let fallbackChannelName = 'Unknown Channel';
  if (!selectedChannel && filters.channel && data?.data) {
    const found = data.data.find((item) => item.channel_details?._id === filters.channel);
    if (found) {
      fallbackChannelName = found.channel_details.channel_name;
    }
  }
  const displayItems = selectedChannel
    ? channelItems
    : filters.channel
    ? [...channelItems, { id: filters.channel, value: fallbackChannelName }]
    : channelItems;

  // PAGE HEADER (title/subtitle overrides + Create button rendered into the global header's action slot)
  const history = useHistory();
  const headerActionEl = useAdsPageHeader(
    'TV Program Setup',
    'Configure channel recording settings, program details and automation schedules.',
  );
  const handleCreate = () =>
    history.push(ROUTES.TV.PROGRAMSETUP.CREATE, {
      defaultChannelId: filters.channel,
      defaultChannelName: displayItems.find((item) => item.id === filters.channel)?.value,
    });

  return (
    <ListPage>
      {headerActionEl &&
        createPortal(<CreateButton onClick={handleCreate}>Create New Program Setup</CreateButton>, headerActionEl)}
      <ListBar>
        <FilterFld>
          <FilterLabel>Channel</FilterLabel>
          <CustomSelect
            className="w-full"
            placeHolder="Select Channel"
            items={displayItems}
            value={filters.channel || undefined}
            onChange={(value) => onFilter('channel', value as string)}
            loading={loading.channel}
            allowClear
            showSearch
            filterOption={false}
            onSearch={(value) => onSearch('channel', value)}
          />
        </FilterFld>
        {selectedRowKeys.length ? (
          <Space>
            <div>
              <Form layout="vertical">
                <Form.Item
                  label={`Move to other ${locationState?.machine_type === 'recording' ? 'Local' : ''} ${toTitleCase(
                    locationState?.machine_type,
                  )} Machine`}
                >
                  <Select
                    className="min-w-[200px]"
                    placeholder="Select IP Address"
                    options={IPOption}
                    allowClear
                    showSearch
                    onChange={handleChangeIP}
                    value={selectedIP || undefined}
                  ></Select>
                </Form.Item>
              </Form>
            </div>
            <Button type="primary" className="mt-[7px]" disabled={selectedIP?.length === 0} onClick={transferMachineIP}>
              Move
            </Button>
          </Space>
        ) : (
          ''
        )}
      </ListBar>
      {filters.channel && (
        <ProgramSetupChannelSettings
          mode="tv"
          channelId={filters.channel}
          channelName={displayItems.find((item) => item.id === filters.channel)?.value}
          representative={channelRepresentative}
          loading={channelSettingsLoading}
        />
      )}
      <TableCard>
        <Table
          loading={loading.programSetup}
          data={tableData}
          rowSelection={locationState?.machine_ip ? rowSelection : false}
          scrollX={false}
          columns={LIST_COLUMN({
            handleEdit: onEdit,
            handleDelete: onDelete,
            filters,
            onFilter,
            search,
            onSearch,
            options,
            loading,
            locationState,
            programById,
          })}
          pagination={{
            current: pagination.page,
            pageSize: pagination.pageSize,
            total: data?.meta?.pagination.total || 0,
            onChange: (newPage: number, currentPageSize: number) => {
              pagination.handlePageChange(newPage);
              pagination.handlePageSizeChange(currentPageSize);
            },
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} results`,
            position: ['topRight', 'bottomRight'],
          }}
        />
      </TableCard>
    </ListPage>
  );
};

export default ListView;
